import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { MessageCircle, Pin, Trash2, Send, Plus, ArrowLeft, User, Shield } from "lucide-react";
import api from "../../lib/api";


const roleColors = { super_admin: "#C7322A", tenant_admin: "#1B4F8A", manager: "#D97706", learner: "#0E8A55" };

const DiscussionForumPage = () => {
  const { token, user } = useAuth();
  const { courseId } = useParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", body: "" });
  const [replyText, setReplyText] = useState("");
  const [courseTitle, setCourseTitle] = useState("");

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.get("/discussions/${courseId}");
      setThreads(res.data.threads || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, courseId]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await api.get("/courses/${courseId}");
        setCourseTitle(res.data?.title || courseId);
      } catch (e) { setCourseTitle(courseId); }
    };
    fetchCourse();
  }, [courseId]);

  const loadThread = async (threadId) => {
    try {
      const res = await api.get("/discussions/thread/${threadId}");
      setThreadDetail(res.data);
      setSelectedThread(threadId);
    } catch (e) { toast.error("Failed to load thread"); }
  };

  const createThread = async () => {
    if (!createForm.title.trim() || !createForm.body.trim()) return toast.error("Title and body are required");
    try {
      await axios.post(`${API}/discussions/${courseId}`, { ...createForm, course_id: courseId }, { headers });
      toast.success("Discussion thread created");
      setShowCreate(false);
      setCreateForm({ title: "", body: "" });
      fetchThreads();
    } catch (e) { toast.error("Failed to create thread"); }
  };

  const postReply = async () => {
    if (!replyText.trim()) return;
    try {
      await api.post("/discussions/thread/${selectedThread}/reply", { body: replyText });
      setReplyText("");
      loadThread(selectedThread);
    } catch (e) { toast.error("Failed to post reply"); }
  };

  const deleteThread = async (threadId) => {
    try {
      await api.delete("/discussions/thread/${threadId}");
      toast.success("Thread deleted");
      if (selectedThread === threadId) { setSelectedThread(null); setThreadDetail(null); }
      fetchThreads();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const togglePin = async (threadId) => {
    try {
      await api.patch("/discussions/thread/${threadId}/pin", {});
      fetchThreads();
    } catch (e) { toast.error("Failed to toggle pin"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Discussion Forum">
    <div className="p-6 space-y-6" data-testid="discussion-forum-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={-1} className="p-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Discussion Forum</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{courseTitle}</p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-thread-btn">
          <Plus className="w-4 h-4 mr-2" /> New Discussion
        </Button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="create-thread-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Start a Discussion</h2>
            <div className="space-y-3">
              <Input value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})} placeholder="Discussion title..." data-testid="thread-title-input" />
              <textarea className="w-full rounded-md border p-3 text-sm bg-background min-h-[120px] resize-y" value={createForm.body}
                onChange={e => setCreateForm({...createForm, body: e.target.value})} placeholder="What would you like to discuss?" data-testid="thread-body-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={createThread} data-testid="submit-thread-btn">Post Discussion</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Thread List */}
        <div className="w-[400px] space-y-2 flex-shrink-0">
          {threads.length === 0 ? (
            <div className="border rounded-xl p-8 text-center text-muted-foreground">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No discussions yet</p>
              <p className="text-sm mt-1">Start a conversation about this course</p>
            </div>
          ) : threads.map(t => (
            <div key={t.id}
              className={`border rounded-xl p-3 cursor-pointer transition-colors ${selectedThread === t.id ? 'border-orange-400 bg-orange-50/50' : 'hover:bg-muted/30'}`}
              onClick={() => loadThread(t.id)} data-testid={`thread-${t.id}`}>
              <div className="flex items-start gap-2">
                {t.is_pinned && <Pin className="w-3.5 h-3.5 text-orange-500 mt-1 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{t.body}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: `${roleColors[t.author_role] || '#666'}15`, color: roleColors[t.author_role] || '#666' }}>
                      {t.author_name}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{t.reply_count || 0} replies</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Thread Detail */}
        <div className="flex-1">
          {!threadDetail ? (
            <div className="border rounded-xl p-12 text-center text-muted-foreground">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a discussion to view</p>
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden shadow-sm" data-testid="thread-detail">
              {/* Thread Header */}
              <div className="p-4 border-b bg-muted/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-bold text-lg">{threadDetail.thread.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: `${roleColors[threadDetail.thread.author_role] || '#666'}15`, color: roleColors[threadDetail.thread.author_role] || '#666' }}>
                        {threadDetail.thread.author_name}
                      </span>
                      <span className="text-xs text-muted-foreground">{new Date(threadDetail.thread.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {["super_admin", "tenant_admin"].includes(user?.role) && (
                      <Button variant="ghost" size="sm" onClick={() => togglePin(threadDetail.thread.id)} className="h-8">
                        <Pin className={`w-4 h-4 ${threadDetail.thread.is_pinned ? 'text-orange-500' : ''}`} />
                      </Button>
                    )}
                    {(threadDetail.thread.author_id === user?.id || ["super_admin", "tenant_admin"].includes(user?.role)) && (
                      <Button variant="ghost" size="sm" onClick={() => deleteThread(threadDetail.thread.id)} className="h-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
                <p className="text-sm mt-3">{threadDetail.thread.body}</p>
              </div>

              {/* Replies */}
              <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                {(threadDetail.replies || []).map(r => (
                  <div key={r.id} className="flex gap-3" data-testid={`reply-${r.id}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: `${roleColors[r.author_role] || '#666'}20`, color: roleColors[r.author_role] || '#666' }}>
                      {r.author_name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 bg-muted/30 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs">{r.author_name}</span>
                        <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm mt-1">{r.body}</p>
                    </div>
                  </div>
                ))}
                {(threadDetail.replies || []).length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4">No replies yet. Be the first to respond!</p>
                )}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t flex gap-2">
                <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write a reply..." className="flex-1" data-testid="reply-input"
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && postReply()} />
                <Button className="btn-primary" onClick={postReply} data-testid="send-reply-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default DiscussionForumPage;
